
import { useEffect, useState } from "react";
import { AdminSidebar } from "@/components/admin/AdminSidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Package, DollarSign, AlertCircle, Users, Archive } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { formatCurrency } from "@/lib/utils";
import { Slab } from "@/types/slab";

interface CategoryCount {
  type: string;
  count: number;
}

export default function Admin() {
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalValue, setTotalValue] = useState(0);
  const [lowStockCount, setLowStockCount] = useState(0);
  const [outOfStockCount, setOutOfStockCount] = useState(0);
  const [activeUsers, setActiveUsers] = useState(0);
  const [categoryCounts, setCategoryCounts] = useState<CategoryCount[]>([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Buscar produtos
      const { data: slabs, error: slabsError } = await supabase
        .from('cat_slabs')
        .select('*');

      if (slabsError) throw slabsError;

      const slabsData = slabs as any[] || [];
      
      // Calcular totais
      setTotalProducts(slabsData.length);
      
      const total = slabsData.reduce((sum, slab) => 
        sum + (Number(slab.price) || 0), 0
      );
      setTotalValue(total);

      // Contar produtos com estoque baixo e fora de estoque
      const lowStock = slabsData.filter(slab => 
        Number(slab.quantity) > 0 && Number(slab.quantity) < 2
      ).length;
      const outOfStock = slabsData.filter(slab => 
        Number(slab.quantity) === 0
      ).length;
      
      setLowStockCount(lowStock);
      setOutOfStockCount(outOfStock);

      // Contar por categoria
      const categories = slabsData.reduce((acc: Record<string, number>, slab) => {
        const type = slab.type || 'Other';
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      }, {});

      // Converter explicitamente para o tipo CategoryCount[]
      const categoryCountsData: CategoryCount[] = Object.entries(categories).map(([type, count]) => ({
        type,
        count: Number(count)
      }));

      setCategoryCounts(categoryCountsData);

      // Buscar usuários ativos
      const { data: users, error: usersError } = await supabase
        .from('cat_users')
        .select('*')
        .eq('active', true);

      if (usersError) throw usersError;

      setActiveUsers(users?.length || 0);

    } catch (error) {
      console.error('Erro ao carregar dados do dashboard:', error);
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      <AdminSidebar />
      
      <div className="flex-1 ml-[250px]">
        <div className="p-6">
          <h1 className="text-3xl font-bold mb-6">Dashboard</h1>
          
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Total de Produtos
                </CardTitle>
                <Package className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{totalProducts}</div>
                <p className="text-xs text-muted-foreground">
                  Produtos cadastrados
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Valor Total
                </CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatCurrency(totalValue)}</div>
                <p className="text-xs text-muted-foreground">
                  Em produtos
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Estoque Baixo
                </CardTitle>
                <AlertCircle className="h-4 w-4 text-amber-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{lowStockCount}</div>
                <p className="text-xs text-muted-foreground">
                  Produtos com estoque baixo
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Fora de Estoque
                </CardTitle>
                <AlertCircle className="h-4 w-4 text-red-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{outOfStockCount}</div>
                <p className="text-xs text-muted-foreground">
                  Produtos sem estoque
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-6">
            {categoryCounts.map((category) => (
              <Card key={category.type}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    {category.type}
                  </CardTitle>
                  <Archive className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{category.count}</div>
                  <p className="text-xs text-muted-foreground">
                    Produtos nesta categoria
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Usuários
                </CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{activeUsers}</div>
                <p className="text-xs text-muted-foreground">
                  Usuários ativos
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
